import type { SetupApplyIn, SetupDraftOut } from "@sagot/api-client";
import { SETUP_MAX_LEAD_STAGES, SETUP_MIN_LEAD_STAGES } from "@sagot/constants";

export const PERSONA_MAX_LENGTH = 2_000;
/** 1-based wizard position of the Configure step (what `onboarding.step` stores). */
export const CONFIGURE_STEP = 3;
const ALWAYS_ON = new Set(["human_handoff"]);

export interface StageDraft {
  id: string;
  name: string;
  is_won: boolean;
  is_lost: boolean;
}

export interface ActionDraft {
  key: string;
  name: string;
  enabled: boolean;
}

/** Everything step 3 lets the user edit before confirming (nothing is saved until then). */
export interface SetupConfig {
  template_key: string;
  assistant_name: string;
  instructions: string;
  starter_questions: string[];
  audience_labels: string[];
  audiences: number[];
  lead_stages: StageDraft[];
  actions: ActionDraft[];
  welcome_message: string;
  blocked_topics: string;
}

let stageSeq = 0;
export const newStageId = () => `stage-${String((stageSeq += 1))}`;

export function configFromDraft(draft: SetupDraftOut): SetupConfig {
  return {
    template_key: draft.template_key,
    assistant_name: draft.assistant_name,
    instructions: draft.instructions,
    starter_questions: [...draft.starter_questions],
    audience_labels: [...draft.audience_labels],
    audiences: [...draft.audiences],
    lead_stages: draft.lead_stages.map((stage) => ({ ...stage, id: newStageId() })),
    actions: draft.actions.map((action) => ({ ...action, enabled: action.enabled || ALWAYS_ON.has(action.key) })),
    welcome_message: draft.welcome_message,
    blocked_topics: draft.blocked_topics.join("\n"),
  };
}

export const isAlwaysOn = (key: string) => ALWAYS_ON.has(key);

/** Text of everything editable (stage ids are per load, so they are left out): equal = no edits. */
export function configSignature(config: SetupConfig): string {
  return JSON.stringify({ ...config, lead_stages: config.lead_stages.map(({ name, is_won, is_lost }) => ({ name, is_won, is_lost })) });
}

/** Mirrors the API pipeline rule: first open, ≥ 1 won, exactly 1 lost, unique names. */
export function stagesValid(stages: readonly StageDraft[]): boolean {
  const names = stages.map((s) => s.name.trim().toLowerCase());
  if (stages.length < SETUP_MIN_LEAD_STAGES || stages.length > SETUP_MAX_LEAD_STAGES) return false;
  if (names.some((n) => !n) || new Set(names).size !== names.length) return false;
  const [first] = stages;
  if (!first || first.is_won || first.is_lost) return false;
  return stages.some((s) => s.is_won) && stages.filter((s) => s.is_lost).length === 1;
}

export function configProblems(config: SetupConfig): string[] {
  const problems: string[] = [];
  if (!config.assistant_name.trim()) problems.push("name");
  if (!config.instructions.trim() || config.instructions.length > PERSONA_MAX_LENGTH) problems.push("instructions");
  if (!config.starter_questions.some((q) => q.trim())) problems.push("starters");
  if (config.audience_labels.some((label) => !label.trim()) || config.audiences.length === 0) problems.push("audiences");
  if (!stagesValid(config.lead_stages)) problems.push("stages");
  if (!config.welcome_message.trim()) problems.push("welcome");
  return problems;
}

export function applyBody(config: SetupConfig): SetupApplyIn {
  return {
    template_key: config.template_key as SetupApplyIn["template_key"],
    assistant_name: config.assistant_name.trim(),
    instructions: config.instructions.trim(),
    starter_questions: config.starter_questions.map((q) => q.trim()).filter(Boolean),
    audience_labels: config.audience_labels.map((label) => label.trim()),
    audiences: [...config.audiences].sort(),
    lead_stages: config.lead_stages.map(({ name, is_won, is_lost }) => ({ name: name.trim(), is_won, is_lost })),
    actions: config.actions.map(({ key, enabled }) => ({ key: key as SetupApplyIn["actions"][number]["key"], enabled })),
    welcome_message: config.welcome_message.trim(),
    blocked_topics: config.blocked_topics.split("\n").map((topic) => topic.trim()).filter(Boolean).slice(0, 10),
    apply_workspace: true,
  };
}
